import React, { useMemo } from 'react';
import usePatternMemory from '../../hooks/usePatternMemory';

/**
 * CycleTimelineScreen — the story of the user's body this cycle.
 *
 * Instead of a chronological log, this weaves recovery, energy, sleep
 * and symptom changes into an editorial narrative — each day-event
 * carries an icon, a short explanation and (when data supports it) a
 * Theo insight line.
 *
 * Zero new tables — reads symptom_logs + daily_checkins via
 * usePatternMemory + derives day-of-cycle from user_cycle_data (props).
 */

const DAY_MS = 86400_000;

function buildTimeline({ symptomHistory, checkins, recoverySeries, cycleStart, cycleLength }) {
  if (!cycleStart) return [];
  const start = new Date(cycleStart).getTime();
  const events = [];

  const inCurrentCycle = (isoDate) => {
    const t = new Date(isoDate).getTime();
    return t >= start && t <= start + (cycleLength + 5) * DAY_MS;
  };
  const dayOf = (isoDate) => Math.floor((new Date(isoDate).getTime() - start) / DAY_MS) + 1;

  // 1) Recovery inflection points inside current cycle
  const rec = recoverySeries.filter(r => inCurrentCycle(r.date));
  for (let i = 1; i < rec.length - 1; i++) {
    const prev = rec[i - 1].score, curr = rec[i].score, next = rec[i + 1].score;
    if (curr - prev >= 12 && next >= curr - 3) {
      events.push({
        day: dayOf(rec[i].date), icon: 'up', tone: 'brand',
        title: 'Recovery improving.',
        body: `You rebounded from ${prev} → ${curr} over 24 hours.`,
        theo: 'Sleep and low-fatigue days compounded here.',
      });
    } else if (prev - curr >= 12) {
      events.push({
        day: dayOf(rec[i].date), icon: 'down', tone: 'warn',
        title: 'Recovery dipped.',
        body: `A ${prev - curr}-point drop suggests the day cost more than the previous.`,
        theo: null,
      });
    }
  }

  // 2) Sleep decline
  const slp = symptomHistory.filter(r => inCurrentCycle(r.log_date) && r.sleep_quality != null);
  for (let i = 2; i < slp.length; i++) {
    const prev2 = ((slp[i - 1].sleep_quality || 5) + (slp[i - 2].sleep_quality || 5)) / 2;
    if (prev2 - (slp[i].sleep_quality || 5) >= 2.5) {
      events.push({
        day: dayOf(slp[i].log_date), icon: 'moon', tone: 'quiet',
        title: 'Sleep declined.',
        body: 'Sleep quality dropped two days in a row.',
        theo: 'Common in mid-luteal — progesterone can disturb sleep.',
      });
      break;
    }
  }

  // 3) Energy peak
  const eng = checkins.filter(c => inCurrentCycle(c.log_date) && c.energy_level != null);
  const peak = eng.reduce((best, c) => (c.energy_level > (best?.energy_level ?? -1) ? c : best), null);
  if (peak && peak.energy_level >= 7) {
    events.push({
      day: dayOf(peak.log_date), icon: 'sun', tone: 'brand',
      title: 'Energy peaked.',
      body: `Energy hit ${peak.energy_level}/10 — the highest logged this cycle.`,
      theo: 'Follicular estrogen supports higher output windows.',
    });
  }

  // 4) Ovulation likely
  const cycleDay = Math.floor((Date.now() - start) / DAY_MS) + 1;
  if (cycleDay >= 14 && cycleLength >= 14) {
    events.push({
      day: 14, icon: 'target', tone: 'brand',
      title: 'Ovulation likely approaching.',
      body: 'Mid-cycle — LH surge window.',
      theo: 'Based on a typical 28-day pattern.',
    });
  }

  // 5) Symptom settling
  const symsWindow = symptomHistory.filter(r => inCurrentCycle(r.log_date));
  if (symsWindow.length >= 6) {
    const totalScore = (r) => (r.acne || 0) + (r.hair_loss || 0) + (r.mood || 0) + (r.fatigue || 0) + (r.weight_change || 0);
    const first3 = symsWindow.slice(0, 3).map(totalScore);
    const last3  = symsWindow.slice(-3).map(totalScore);
    const avg = (a) => a.reduce((x, y) => x + y, 0) / a.length;
    if (avg(first3) - avg(last3) >= 3) {
      events.push({
        day: dayOf(symsWindow[symsWindow.length - 1].log_date), icon: 'settle', tone: 'brand',
        title: 'Symptoms beginning to settle.',
        body: `Total symptom load fell from ~${avg(first3).toFixed(0)} to ${avg(last3).toFixed(0)}.`,
        theo: null,
      });
    }
  }

  // Today marker (last)
  events.push({
    day: cycleDay, icon: 'now', tone: 'brand',
    title: 'Today.',
    body: 'The story continues.',
    theo: null,
    isToday: true,
  });

  // Sort by day
  return events.sort((a, b) => a.day - b.day);
}

const ICONS = {
  up: '↑', down: '↓', moon: '☾', sun: '☀', target: '⊚', settle: '≈', now: '●',
};

/** Narrative bridge — connects two adjacent events with a natural sentence. */
function narrativeBridge(prev, next) {
  const dayGap = Math.max(1, next.day - prev.day);
  const daysWord = dayGap === 1 ? '1 day later' : `${dayGap} days later`;
  const pairs = {
    up_down:    `${daysWord}, the momentum reversed.`,
    up_target:  `${daysWord}, ovulation approached — the shift makes sense.`,
    up_settle:  `${daysWord}, symptoms started to settle in step.`,
    up_now:     '',
    down_up:    `${daysWord}, energy climbed again.`,
    down_moon:  `${daysWord}, sleep also softened.`,
    down_now:   '',
    moon_sun:   `${daysWord}, energy peaked despite the sleep dip.`,
    moon_now:   '',
    sun_target: `${daysWord}, the mid-cycle window opened.`,
    sun_settle: `${daysWord}, symptoms eased as the body rebalanced.`,
    sun_now:    '',
    target_settle: `${daysWord}, the second half of the cycle steadied.`,
    target_now: `${daysWord}, we arrived at today.`,
    settle_now: `${daysWord}, the calmer window continues.`,
  };
  const key = `${prev.icon}_${next.icon}`;
  if (pairs[key]) return pairs[key];
  return `${daysWord}, the body\u2019s pattern kept shifting.`;
}

export default function CycleTimelineScreen({ userId, dayNumber, cycleLength, expectedNext, cycleStartDate }) {
  const { symptomHistory, checkins, recoverySeries, loading } = usePatternMemory(userId);

  const cycleStart = cycleStartDate
    ? cycleStartDate
    : (expectedNext ? new Date(expectedNext.getTime() - (cycleLength || 28) * DAY_MS).toISOString().split('T')[0] : null);

  const events = useMemo(
    () => buildTimeline({ symptomHistory, checkins, recoverySeries, cycleStart, cycleLength: cycleLength || 28 }),
    [symptomHistory, checkins, recoverySeries, cycleStart, cycleLength]
  );

  return (
    <div className="cx-sub-body" data-testid="cycle-timeline-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">CURRENT CYCLE · DAY {dayNumber}</div>
        <h1 className="cx-intel-h1">The story so far.</h1>
        <p className="cx-sentence" style={{ marginTop: 14, maxWidth: 340 }}>
          {events.length > 1
            ? 'Recovery, energy and symptoms — woven into the arc of this cycle.'
            : 'As you log, this timeline will fill with the moments Theo notices.'}
        </p>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Timeline</div>
        <div className="cx-timeline" data-testid="timeline-list">
          {loading ? (
            <div className="cx-empty-block cx-glass" style={{ padding: 18, borderRadius: 20 }}>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--cx-mute)' }}>Reading your history\u2026</p>
            </div>
          ) : events.map((e, i) => (
            <React.Fragment key={i}>
              <div className={`cx-timeline-row cx-timeline-row--${e.tone || 'brand'} ${e.isToday ? 'cx-timeline-row--today' : ''}`} data-testid={`timeline-day-${e.day}`}>
                <div className="cx-timeline-mark">
                  <span className="cx-timeline-day">Day {e.day}</span>
                  <span className="cx-timeline-icon">{ICONS[e.icon] || '·'}</span>
                </div>
                <div className="cx-timeline-body">
                  <div className="cx-timeline-title">{e.title}</div>
                  <div className="cx-timeline-sub">{e.body}</div>
                  {e.theo && (
                    <div className="cx-timeline-theo">
                      <span className="cx-theo-tag">THEO</span> {e.theo}
                    </div>
                  )}
                </div>
              </div>
              {i < events.length - 1 && !events[i + 1].isToday && (
                <div className="cx-timeline-bridge">{narrativeBridge(e, events[i + 1])}</div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>
    </div>
  );
}
